import React, { useEffect, useState, useCallback } from 'react';
import PropTypes from 'prop-types';
import _ from 'lodash';
import { Redirect, withRouter } from 'react-router-dom';
import { Wizard } from '@xengage/gw-portals-wizard-react';
import { withViewModelService } from '@xengage/gw-portals-viewmodel-react';
import { useAuthentication } from '@xengage/gw-digital-auth-react';
import { ClauseService } from 'gw-capability-policycommon';
import { ClausesUtil } from '@xengage/gw-policycommon-util-js';
import { LoadSaveService } from 'gw-capability-gateway-quoteandbind';
import { ErrorBoundary } from '@xengage/gw-portals-error-react';
import { messages as commonMessages } from '@xengage/gw-platform-translations';
import { Loader, useModal } from '@jutro/components';
import { MockUpUtil } from '@xengage/gw-portals-util-js';
import wizardStepToFieldMapping from './config/pe-bop-wizard-step-to-field-mapping.json5';

import wizardConfig from './config/pe-bop-wizard-config.json5';
import pebopmessages from './PEBOPWizard.messages';

const PATH_TO_MOCK_DATA = 'quote.bop';
const MOCK_DATA_TO_SET = [
    'bindData.contactPhone',
    'lobData.businessOwners.accountOrgType',
    'lobData.businessOwners.smallBusinessType'
];
const MOCK_DATA_TO_REMOVE = ['bindData.contactPhone'];

function PEBOPWizard(props) {
    const modalApi = useModal();
    const { steps, title } = wizardConfig;
    const { authHeader } = useAuthentication();
    const [initialSubmission, setInitialSubmission] = useState(null);
    const [hasErrorOccurred, setHasErrorOccurred] = useState(false);
    const [isLoading, setIsLoading] = useState(true);
    const { viewModelService, history, location } = props;

    useEffect(
        () => {
            const viewModelContext = {
                AccountEmailRequired: false,
                AccountDOBRequired: false,
                DriverEmailRequired: true
            };
            if (!location.state) {
                history.push('/');
                return;
            }
            const { postalCode, quoteID } = location.state.quoteentry;
            const requestData = {
                quoteID: quoteID,
                postalCode: postalCode
            };
            LoadSaveService.retrieveSubmission(requestData, authHeader)
                .then((response) => {
                    return MockUpUtil.cleanUpMockedProperties(
                        response,
                        PATH_TO_MOCK_DATA,
                        ...MOCK_DATA_TO_REMOVE
                    );
                })
                .then((response) => {
                    ClausesUtil.getDependentClausesFromServer(
                        ClauseService,
                        _.get(response, 'quoteID'),
                        authHeader
                    );
                    const submission = viewModelService.create(
                        response,
                        'pc',
                        'edge.capabilities.quote.submission.dto.QuoteDataDTO',
                        viewModelContext
                    );
                    setInitialSubmission(submission);
                    setIsLoading(false);
                });
        },
        // only execute once
        // eslint-disable-next-line react-hooks/exhaustive-deps
        []
    );

    const handleCancel = useCallback(
        ({ wizardSnapshot, param: nextPath = '', wizardData }) => {
            const skipModal = ['knockoutpage', 'success', 'logout'];
            const lastPathName = nextPath.replace(/\/?.*\//g, '');
            const quoteSummaryRegex = /^\/quotes\/[0-9]+\/summary/;
            const periodStatus = _.get(wizardSnapshot, 'baseData.periodStatus.value.code');

            if (
                periodStatus === 'Bound'
                || skipModal.includes(lastPathName)
                || nextPath.match(quoteSummaryRegex)
                || hasErrorOccurred
            ) {
                // allow transition once the submission is bound
                // or returning to quote summary page
                return true;
            }

            return modalApi.showConfirm({
                title: commonMessages.wantToCancel,
                message: commonMessages.infoWillBeSavedAsDraft,
                status: 'warning',
                icon: 'mi-error-outline',
                confirmButtonText: commonMessages.yes,
                cancelButtonText: commonMessages.no
            }).then((results) => {
                if (results === 'cancel') {
                    return _.noop();
                }
                const isStatusQuoted = _.get(wizardSnapshot, 'baseData.periodStatus.value.code') === 'Quoted';

                let serverCall = LoadSaveService.updateDraftSubmission;

                if (isStatusQuoted) {
                    serverCall = LoadSaveService.updateQuotedSubmission;
                }

                const isValid = wizardData.lobData.aspects.valid
                    && wizardData.lobData.aspects.subtreeValid;
                const payload = isValid
                    ? _.get(wizardData, 'value')
                    : _.get(wizardSnapshot, 'value');
                const payloadBeforeSave = _.omit(payload, 'bindData.paymentDetails');
                const dataToSave = MockUpUtil.setMockData(
                    payloadBeforeSave,
                    PATH_TO_MOCK_DATA,
                    ...MOCK_DATA_TO_SET
                );

                setIsLoading(true);
                const redirectPath = `/quotes/${dataToSave.quoteID}/summary`;
                serverCall(dataToSave, authHeader)
                    .then(() => {
                        history.push(nextPath || redirectPath);
                    })
                    .catch(() => {
                        modalApi.showAlert({
                            title: pebopmessages.anErrorOccurredTitle,
                            message: pebopmessages.anErrorOccurred,
                            status: 'error',
                            icon: 'mi-error-outline',
                            confirmButtonText: commonMessages.ok
                        }).catch(_.noop);
                        history.push(nextPath || redirectPath);
                    });
                return true;
            }, _.noop);
        },
        [hasErrorOccurred, authHeader, history]
    );

    const handleError = useCallback(
        (error) => {
            const isQuotePage = _.includes(_.toLower(_.get(error, 'gwInfo.method')), 'quote');
            const quoteIDFromWizard = _.get(error, 'gwInfo.params[0].quoteID');
            const quoteIDFromLocation = _.get(location, 'state.quoteentry.quoteID');
            const quoteID = quoteIDFromWizard || quoteIDFromLocation;
            const redirectPath = `/quotes/${quoteID}/summary`;
            const state = {
                pathname: redirectPath,
                state: error
            };

            return modalApi.showAlert({
                title: isQuotePage
                    ? pebopmessages.saveQuoteError
                    : pebopmessages.anErrorOccurredTitle,
                message: isQuotePage
                    ? pebopmessages.saveQuoteErrorMessage
                    : pebopmessages.anErrorOccurred,
                status: 'error',
                icon: 'mi-error-outline',
                confirmButtonText: commonMessages.ok
            }).then(() => {
                setHasErrorOccurred(true);
                return <Redirect to={state} />;
            }, _.noop);
        },
        [location]
    );

    const handleKnockout = useCallback(({ knockOutErrors }) => {
        history.push('/knockoutpage', { underwritingIssues: knockOutErrors });
    }, [history]);

    if (isLoading) {
        return <Loader loaded={!isLoading} />;
    }

    if (!initialSubmission) {
        return null;
    }

    return (
        <ErrorBoundary onError={handleError}>
            <Wizard
                initialSteps={steps}
                wizardTitle={title}
                onKnockOut={handleKnockout}
                initialData={initialSubmission}
                onCancel={handleCancel}
                skipCompletedSteps
                wizardStepToFieldMapping={wizardStepToFieldMapping}
            />
        </ErrorBoundary>
    );
}

PEBOPWizard.propTypes = {
    viewModelService: PropTypes.shape({
        create: PropTypes.func
    }).isRequired,
    history: PropTypes.shape({
        push: PropTypes.func
    }).isRequired,
    location: PropTypes.shape({
        state: PropTypes.shape({
            quoteentry: PropTypes.shape({
                postalCode: PropTypes.string,
                quoteID: PropTypes.string
            })
        })
    }).isRequired
};

export default withRouter(withViewModelService(PEBOPWizard));
